// Single source of truth for task points.
//
// Where points live (decided with Maximilian) — no ghost nodes, no extra fields; every task stays a
// normal node so all downstream label-matching/display works unchanged:
//  - `ExamTask.declaredMaxPoints` — the DECLARED TOTAL of a task (a leaf's points, or a parent's whole
//    subtree total). Written by extraction / the structure editor.
//  - `gradingScheme.maxPoints` — the OWN points of an ANSWERABLE task (a leaf's points; an answerable
//    parent's own-answer points = total − children). Stored explicitly (a points-only scheme is created
//    on save; the GS agent later fills in criteria for that same own value). So a hybrid answerable
//    parent carries BOTH: total in `declaredMaxPoints`, own in `gradingScheme.maxPoints`.
//    Invariant (enforced in the editor): either a task has ALL points (own + every child, summing to the
//    total) or ONLY the total (top-down, agent distributes) — nothing partial.
//  - `ExamTask.aggregateMaxPoints` — DERIVED, never stored: a baseql field RESOLVER computes it on read
//    as `computeAggregate(this)`. Nothing to sync, so it can never go stale.
//
// `computeAggregate` is the single pure source of truth, called both by that resolver and by any
// server code holding a raw task tree. `findPointInconsistencies` powers the structure-editor
// consistency flag / confirm block: it flags a parent whose DECLARED total contradicts the real sum.

/** Nodes are raw DB exam-task objects (recursive `subTasks`). Kept loose to match callers' `any` trees. */
type TaskNode = {
  _id?: string;
  taskNumber?: string;
  needsAnswer?: boolean | null;
  gradingScheme?: any;
  declaredMaxPoints?: number | null;
  subTasks?: TaskNode[] | null;
  [key: string]: any;
};

/** GradingScheme is stored as a plain embedded object, but some read paths wrap it as `{ doc }`. */
function readGradingScheme(node: TaskNode | undefined): any | undefined {
  const gs = node?.gradingScheme;
  return gs?.doc ?? gs;
}

/** True for aggregator (non-answerable) nodes, which carry no own points. */
export function isAggregator(node: TaskNode | undefined): boolean {
  return node?.needsAnswer === false;
}

/** Numeric grading-scheme maxPoints, or undefined if no scheme / unparseable. */
function schemeMaxPoints(node: TaskNode | undefined): number | undefined {
  const raw = readGradingScheme(node)?.maxPoints;
  if (raw == null || raw === '') return undefined;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

/** Numeric declared (sheet/teacher) TOTAL, or undefined if unset. */
function declaredMaxPoints(node: TaskNode | undefined): number | undefined {
  const raw = node?.declaredMaxPoints;
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : undefined;
}

/** True when the node has at least one subtask. */
function hasChildren(node: TaskNode | undefined): boolean {
  return Array.isArray(node?.subTasks) && node!.subTasks!.length > 0;
}

/** Sum of the resolved subtree totals of a node's direct children. */
function childrenAggregate(node: TaskNode | undefined): number {
  const children = Array.isArray(node?.subTasks) ? node!.subTasks! : [];
  return children.reduce((sum, child) => sum + computeAggregate(child), 0);
}

/**
 * A node's OWN points (its own answer worth, excluding children), read from its grading scheme. A TASK_FRAME
 * carries none (0). An answerable LEAF's own = scheme ?? declared. An answerable PARENT's own = its scheme
 * maxPoints (the stored own value); before a scheme exists it falls back to the derived remainder
 * (total − children).
 */
export function ownMaxPoints(node: TaskNode | undefined): number {
  if (isAggregator(node)) return 0;
  if (!hasChildren(node)) return schemeMaxPoints(node) ?? declaredMaxPoints(node) ?? 0;
  const scheme = schemeMaxPoints(node);
  if (scheme != null) return scheme;
  const total = declaredMaxPoints(node);
  return total != null ? Math.max(0, total - childrenAggregate(node)) : 0;
}

/**
 * The declared/known value for a node — used to prompt the GS agent (its OWN target) and to detect its
 * adjustments. A task frame uses its declared TOTAL; an answerable node uses its OWN points (scheme if set,
 * else declared). For a top-down parent (own not set, only a total) this falls back to the total.
 * Undefined when nothing is stated.
 */
export function statedTotal(node: TaskNode | undefined): number | undefined {
  if (isAggregator(node)) return declaredMaxPoints(node);
  return schemeMaxPoints(node) ?? declaredMaxPoints(node);
}

/**
 * The value a node currently resolves to, for comparison against its `statedTotal` snapshot to detect
 * agent point adjustments: a task frame's children sum, an answerable node's own points.
 */
export function effectivePoints(node: TaskNode | undefined): number {
  return isAggregator(node) ? computeAggregate(node) : ownMaxPoints(node);
}

/**
 * The total points for a node's whole subtree. Pure and derived, so it can be computed on read (this is
 * the body of the `aggregateMaxPoints` field resolver) without anything being stored or synced:
 *  - TASK_FRAME → its children sum once any child is pointed (bottom-up), else its declared total (top-down).
 *  - answerable LEAF → its own points (grading scheme once generated, else declared).
 *  - answerable PARENT → its declared TOTAL (own + children); with no declared total, the children sum.
 *    (Own lives in the scheme and is already included in the declared total, so it is NOT re-added here.)
 */
export function computeAggregate(node: TaskNode | undefined): number {
  if (!node) return 0;
  const childSum = childrenAggregate(node);
  // A FIXED declared total is the source of truth for the level above; only a DYNAMIC task frame (no declared
  // total) reports its live child sum. (Mirrors the answerable-parent branch below and the client.)
  if (isAggregator(node)) {
    const total = declaredMaxPoints(node);
    return total != null ? total : childSum;
  }
  if (!hasChildren(node)) return schemeMaxPoints(node) ?? declaredMaxPoints(node) ?? 0;
  // Answerable parent: the stored total; if none yet, own (from the scheme) + children.
  const total = declaredMaxPoints(node);
  if (total != null) return total;
  return (schemeMaxPoints(node) ?? 0) + childSum;
}

// ---------------------------------------------------------------------------
// Achieved (awarded) points — the submission-side mirror of the max-points model.
//
// A student's awarded points live on the SUBMISSION tree (`SubmissionTask`), which structurally mirrors
// the exam task tree via `subTasksSubmission`. Each answerable node carries a `gradingResult` with the
// awarded value; container/task frame nodes are never graded (the grader gates on `needsAnswer`), so they
// carry no `gradingResult` and contribute 0 own — exactly why `computeAchieved` can be self-contained on
// the submission tree, with no exam pairing, just like `computeAggregate` is on the exam tree.
// ---------------------------------------------------------------------------

/** GradingResult is stored as a plain embedded object, but some read paths wrap it as `{ doc }`. */
function readGradingResult(node: TaskNode | undefined): any | undefined {
  const gr = node?.gradingResult;
  return gr?.doc ?? gr;
}

/**
 * A single submission node's OWN awarded points, read from its grading result: the modern
 * `actualPointsGiven`, else the raw `score`, else 0. Never negative. Not clamped to a max here — the exam
 * owns the ceiling; call sites that need to cap the total against the task's max points do so once, at the
 * top, using the max-points model.
 */
export function awardedPoints(node: TaskNode | undefined): number {
  const gr = readGradingResult(node);
  if (!gr) return 0;
  const raw =
    (Number.isFinite(gr.actualPointsGiven) ? Number(gr.actualPointsGiven) : undefined) ??
    (Number.isFinite(gr.score) ? Number(gr.score) : undefined) ??
    0;
  return Number.isFinite(raw) ? Math.max(0, raw) : 0;
}

/**
 * The total awarded points for a submission node's whole subtree — its own awarded points plus the
 * aggregate of every child. Pure and derived (the body of the `aggregateAchievedPoints` field resolver on
 * `SubmissionTask`), so it can be computed on read without anything being stored or synced. The mirror of
 * `computeAggregate`. Self-contained: containers carry no grading result, so `own` is 0 for them and there
 * is no double counting.
 */
export function computeAchieved(node: TaskNode | undefined): number {
  if (!node) return 0;
  const children = Array.isArray(node.subTasksSubmission) ? node.subTasksSubmission : [];
  return children.reduce((sum, child) => sum + computeAchieved(child), awardedPoints(node));
}

/**
 * Post-distribution cleanup, run when a grading scheme is put in place (generation / upload / extraction).
 *
 * A parent's stored `declaredMaxPoints` (its FIXED total) is only meaningful for structure editing and
 * top-down distribution: while editing it lets the editor flag "children don't sum to the intended total",
 * and before distribution it holds the total the GS agent must spread across children. Once the points ARE
 * distributed, that stored total is redundant and can go stale (e.g. the GS agent rebalances children so
 * their sum no longer equals the old declared). Keeping it would make the editor show a stale fixed total /
 * a false inconsistency and make `computeAggregate` return the stale value (declared wins when present).
 *
 * So: drop the stored total on every node whose children are ALL pointed (distribution complete) — the
 * total then becomes fully derived (`computeAggregate` = own + childSum) everywhere: editor, resolver,
 * grading, reports. A parent whose children are NOT all pointed (genuine top-down, not yet distributed)
 * keeps its declared total. Depth-first, mutates the given raw task nodes in place.
 */
export function dropDistributedParentTotals(nodes: TaskNode[] | null | undefined): void {
  if (!Array.isArray(nodes)) return;
  for (const node of nodes) {
    if (!node) continue;
    const children = Array.isArray(node.subTasks) ? node.subTasks : [];
    if (children.length === 0) continue;
    dropDistributedParentTotals(children); // settle descendants first, so each child reports its final total
    if (children.every(child => computeAggregate(child) > 0)) {
      // `null`, not `delete`: a missing key is indistinguishable on the client from "not requested by
      // the query", which permanently throws when an AdornisEntity reads it (isFromNetwork + omitted).
      node.declaredMaxPoints = null;
    }
  }
}

export interface PointInconsistency {
  /** taskNumber path to a task that is missing points within a partially-assigned level. */
  path: string[];
  taskNumber: string;
}

/**
 * Per-level point consistency (mirrors the client `findFlatPointInconsistencies`). Checks only the NEXT
 * level under each parent (its DIRECT children — leaf OR parent alike):
 *  1. MISSING (all-or-nothing): among a parent's direct children (plus the parent's own answer when set),
 *     once ANY carries points, every one must — the empty ones are returned. A child "carries points" when
 *     its subtree total is defined. A level with none pointed is fine (top-down); an unset own is ignored.
 *  2. MISMATCH: a parent with a FIXED declared total whose children are ALL pointed but `own + children`
 *     (own = 0 for a task frame / when unset) does not equal the total.
 */
export function findPointInconsistencies(tasks: TaskNode[] | undefined): PointInconsistency[] {
  const out: PointInconsistency[] = [];
  const hasPoints = (n: TaskNode): boolean => statedTotal(n) != null || computeAggregate(n) > 0;
  const walk = (nodes: TaskNode[] | null | undefined, parentPath: string[], parent: TaskNode | undefined): void => {
    const children = Array.isArray(nodes) ? nodes : [];
    for (const child of children) {
      walk(child?.subTasks, [...parentPath, String(child?.taskNumber ?? '')], child);
    }
    // An answerable parent's OWN answer is just another subtask slot: the slot always exists, and it counts
    // as assigned only when own > 0 (0 = no points assigned).
    const parentAnswerable = !!parent && parent.needsAnswer !== false;
    const parentOwn = parentAnswerable ? (schemeMaxPoints(parent) ?? 0) : 0;
    // MISMATCH: a parent with a FIXED declared total whose children are ALL pointed but own + children
    // does not equal the total.
    if (
      parent &&
      typeof parent.declaredMaxPoints === 'number' &&
      Number.isFinite(parent.declaredMaxPoints) &&
      children.length > 0 &&
      children.every(hasPoints)
    ) {
      const sum = children.reduce((acc, child) => acc + computeAggregate(child), 0);
      if (Math.abs(parentOwn + sum - parent.declaredMaxPoints) > 0.001) {
        out.push({ path: parentPath, taskNumber: String(parent.taskNumber ?? '') });
      }
    }
    // MISSING (all-or-nothing over the NEXT level): every DIRECT child (leaf OR parent) plus the answerable
    // parent's own slot — once any carries points, they all must.
    if (children.length === 0) return;
    const slots = children.map(child => ({ node: child, path: [...parentPath, String(child?.taskNumber ?? '')] }));
    const ownIsSlot = parentAnswerable;
    const ownAssigned = parentOwn > 0;
    const totalSlots = slots.length + (ownIsSlot ? 1 : 0);
    const assignedCount = slots.filter(s => hasPoints(s.node)).length + (ownAssigned ? 1 : 0);
    if (assignedCount === 0 || assignedCount === totalSlots) return; // all-or-nothing → OK
    for (const s of slots) {
      if (!hasPoints(s.node)) out.push({ path: s.path, taskNumber: String(s.node?.taskNumber ?? '') });
    }
    // own is an empty slot among pointed siblings → flag the parent itself.
    if (ownIsSlot && !ownAssigned && parent)
      out.push({ path: parentPath, taskNumber: String(parent.taskNumber ?? '') });
  };
  walk(tasks, [], undefined);
  // A parent can be flagged by both the own-slot and mismatch checks — dedupe by path.
  const seen = new Set<string>();
  return out.filter(issue => {
    const k = issue.path.join('|');
    return seen.has(k) ? false : (seen.add(k), true);
  });
}
